import { useMemo } from "react";
import OrderHeaderTickIcon from "../../assets/svg/OrderHeaderTickIcon";
import dataConstants from "../../constants/dataConstants";
import useQueryParams from "../../services/utils/custom hooks/QueryHelper";
import useMobile from "../../utils/helpers/useMobile";
import MobileStatusHeader from "./MobileStatusHeader";

interface StatusHeaderProps {
  status: number;
  selectedTab?: string;
}

function StatusHeader({
  status,
  selectedTab,
}: Readonly<StatusHeaderProps>): JSX.Element {
  const query = useQueryParams();
  const isMobile = useMobile();

  const statuses = useMemo(() => {
    const base = dataConstants.status.map((option) => {
      const statusValues = query.getAll("status[]");
      if (statusValues.includes(option.value.toString())) {
        return { ...option, selected: true };
      }
      return { ...option };
    });

    const submittedIndex = base.findIndex((item) => item.value === 40);
    const isPendingAuthCurrent = status === 55 || status === 56;

    const foundIndex = isPendingAuthCurrent
      ? submittedIndex
      : base.findIndex((item) => item.value === status);

    return base.map((item, index) => ({
      ...item,
      valid: foundIndex >= 0 && index <= foundIndex,
    }));
  }, [status, query]);

  if (isMobile) {
    return <MobileStatusHeader statuses={statuses} />;
  }

  const lastIndex = statuses.length - 1;

  return (
    <div
      className={
        selectedTab !== "additional-info"
          ? "lg:sticky lg:top-0 bg-white py-5 mt-2 lg:z-20 w-full"
          : "w-full py-5 mt-2"
      }
    >
      <div className="flex items-start w-full">
        {statuses.map((step, index) => {
          const isFirst = index === 0;
          const isLast = index === lastIndex;
          const isCurrentStep = !step.valid && statuses[index - 1]?.valid;

          return (
            <div
              key={step.label}
              className={`relative flex items-start h-[90px] ${
                isLast ? "flex-none" : "flex-1 min-w-0"
              }`}
            >
              <div className="relative flex-shrink-0">
                <div
                  className={`w-[20px] h-[20px] flex flex-col items-center justify-center border rounded-full ${
                    step.valid
                      ? "border-primary bg-primary"
                      : isCurrentStep
                        ? "border-primary"
                        : "border-dropDownBorder"
                  }`}
                >
                  {step.valid ? (
                    <OrderHeaderTickIcon stroke="white" />
                  ) : isCurrentStep ? (
                    <div className="h-[8px] w-[8px] bg-primary rounded-full" />
                  ) : (
                    <OrderHeaderTickIcon stroke={undefined} />
                  )}
                </div>
                <p
                  className={`absolute top-[28px] !text-xs xl:!text-base leading-tight w-[88px] text-royalBlue ${
                    isFirst
                      ? "left-0 text-left"
                      : isLast
                        ? "right-0 text-right"
                        : "left-1/2 -translate-x-1/2 text-center"
                  }`}
                >
                  {step.label}
                </p>
              </div>
              {!isLast && (
                <div
                  className={`h-[1px] flex-1 mt-[10px] ${
                    step.valid ? "bg-primary" : "bg-roundedInputBorder"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default StatusHeader;
